import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import IconPicker from "./IconPicker";
import ImageUploadField from "./ImageUploadField";
import RepeatableListField from "./RepeatableListField";

/** Generic, declarative field editor: given a block type's fieldsSchema
 * (src/cms/blockRegistry.js) and its current values, renders the right
 * input per field kind. This is what lets every block type share one
 * admin form implementation instead of a hand-written form per type. */
export default function BlockFieldsEditor({
  schema,
  values = {},
  onChange,
  onUploadImage,
}) {
  return (
    <div className="space-y-4">
      {schema.map((field) => (
        <div key={field.key}>
          <label className="text-sm font-semibold text-[#0B2421] mb-1.5 block">
            {field.label}
          </label>

          {field.kind === "text" && (
            <Input
              value={values[field.key] || ""}
              onChange={(e) => onChange(field.key, e.target.value)}
              className="h-11 rounded-xl border-gray-200 bg-white"
            />
          )}

          {field.kind === "url" && (
            <Input
              type="text"
              value={values[field.key] || ""}
              onChange={(e) => onChange(field.key, e.target.value)}
              placeholder="/some-page or https://..."
              className="h-11 rounded-xl border-gray-200 bg-white"
            />
          )}

          {field.kind === "textarea" && (
            <Textarea
              value={values[field.key] || ""}
              onChange={(e) => onChange(field.key, e.target.value)}
              rows={4}
              className="rounded-xl border-gray-200 bg-white resize-none"
            />
          )}

          {field.kind === "image" && (
            <ImageUploadField
              value={values[field.key] || ""}
              onChange={(url) => onChange(field.key, url)}
              onUploadImage={onUploadImage}
            />
          )}

          {field.kind === "icon" && (
            <IconPicker
              value={values[field.key]}
              onChange={(name) => onChange(field.key, name)}
            />
          )}

          {field.kind === "select" && (
            <select
              value={values[field.key] || field.options?.[0] || ""}
              onChange={(e) => onChange(field.key, e.target.value)}
              className="w-full h-11 rounded-xl border border-gray-200 bg-white px-3.5 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-[#0B2421]/30"
            >
              {(field.options || []).map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          )}

          {field.kind === "list" && (
            <RepeatableListField
              itemLabel={field.itemLabel}
              itemFields={field.itemFields}
              items={values[field.key] || []}
              onChange={(next) => onChange(field.key, next)}
              onUploadImage={onUploadImage}
            />
          )}

          {field.helpText && (
            <p className="mt-1 text-xs text-gray-400">{field.helpText}</p>
          )}
        </div>
      ))}
    </div>
  );
}
